import { useLingui } from "@lingui/react/macro";
import type { BotRequestItem, CommitmentState } from "@rakazo/contracts";
import { BotAvatar, cn, toast } from "@rakazo/ui-web";
import { Check, ChevronDown, Pause, Play, Square, Undo2 } from "lucide-react";
import type { KeyboardEvent, PointerEvent } from "react";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { getTicketBoardEnabled, subscribeTicketBoardEnabled } from "../../lib/bootstrap";
import type {
  BotTodoDetails,
  BotTodoRow,
  BotTodoStatus,
  TodoDetailWords,
} from "../../lib/bot-activity";
import {
  clampTodoRailWidth,
  formatTodoDetailLine,
  groupTodoRowsByOwner,
  maxTodoRailWidth,
  orderTodoItems,
  TODO_RAIL_KEY_STEP_PX,
  TODO_RAIL_MIN_PX,
  TODO_RAIL_WIDE_PX,
} from "../../lib/bot-activity";
import {
  useBotSessions,
  useBotTodo,
  useBotTodoRequests,
  useTodoRowActions,
} from "../../lib/use-bot-activity";
import { errorText } from "../../lib/user-error";
import { BotSessionsList } from "./bot-sessions";

const LABEL_TONE: Record<BotTodoStatus, string> = {
  todo: "text-foreground",
  doing: "font-semibold text-status-working",
  review: "text-status-review",
  blocked: "text-destructive",
  done: "text-success",
};

/**
 * jhl-Bots (Arbeitsmodell): a ticket in `doing` is working only while a session of it runs.
 * Servers before the commitment state send no `state`; their `doing` still counts as working.
 */
function isWorkingRow(row: BotTodoRow): boolean {
  return row.state ? row.state.state === "running" : row.status === "doing";
}

/** Short time of day for "until" and "next try" in a to-do line. */
function clock(value: string | null): string {
  return value
    ? new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";
}

/**
 * jhl-Bots (Arbeitsmodell): what a to-do line says about where its commitment stands, so a ticket
 * in progress that is not running names why. Null when the line needs no word (running, open, done,
 * or a block whose reason shows already).
 */
function useCommitmentLine(): (state: CommitmentState | undefined) => string | null {
  const { t } = useLingui();
  const busyWith = (trigger: string | null): string => {
    if (trigger === "bot_message") return t`a message from another bot`;
    if (trigger === "tickets") return t`another task`;
    if (trigger === "routine") return t`a routine`;
    return t`a conversation`;
  };
  const waitOn: Record<string, string> = {
    tickets: t`sub-tasks`,
    time: t`a time`,
    command: t`a long command`,
    memory: t`free memory`,
    bot: t`a reply from another bot`,
    quota: t`the model provider's limit`,
    person: t`you`,
  };
  return (state) => {
    if (!state) return null;
    switch (state.state) {
      case "running":
        return state.problem ?? (state.starting ? t`Starting` : null);
      case "queued": {
        const why =
          state.reason === "retry"
            ? t`Next try at ${clock(state.at)}`
            : state.reason === "busy"
              ? t`Up next: busy with ${busyWith(state.busyWith)}`
              : t`Up next`;
        return state.problem ? `${why}. ${state.problem}` : why;
      }
      case "waiting": {
        const on = waitOn[state.on] ?? state.on;
        return state.until
          ? t`Waiting for ${on} until ${clock(state.until)}`
          : t`Waiting for ${on}`;
      }
      case "needs_owner":
        if (state.reason === "card") return t`Needs your answer in the chat`;
        if (state.reason === "person")
          return state.detail ? t`Needs you: ${state.detail}` : t`Needs you`;
        return null;
      default:
        return null;
    }
  };
}

/**
 * RAK-91 M1: the words of the detail line in the active language: units and "of" from the catalog,
 * the cost as the locale writes US dollars ("$3.40", "3,40 $").
 */
function useTodoDetailWords(): TodoDetailWords {
  const { t, i18n } = useLingui();
  const locale = i18n.locale || "en";
  const currency = useMemo(
    () => new Intl.NumberFormat(locale, { style: "currency", currency: "USD" }),
    [locale],
  );
  return {
    progress: (done, total) => t`${done} of ${total}`,
    under: t`under 1 min`,
    unit: ({ value, unit }) =>
      unit === "d" ? t`${value} d` : unit === "h" ? t`${value} h` : t`${value} min`,
    cost: (usd) => currency.format(usd),
  };
}

/** Each status has its own shape so it does not rely on color alone. */
function StatusIcon({ status }: { status: BotTodoStatus }) {
  const common = { width: 12, height: 12, viewBox: "0 0 12 12", "aria-hidden": true } as const;
  switch (status) {
    case "doing":
      return (
        <svg {...common} className="text-status-working">
          <circle cx="6" cy="6" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M6 1.5A4.5 4.5 0 0 1 6 10.5Z" fill="currentColor" />
        </svg>
      );
    case "review":
      return (
        <svg {...common} className="text-status-review">
          <circle cx="6" cy="6" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <circle cx="6" cy="6" r="1.6" fill="currentColor" />
        </svg>
      );
    case "blocked":
      return (
        <svg {...common} className="text-destructive">
          <circle cx="6" cy="6" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M3.3 8.7l5.4-5.4" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      );
    case "done":
      return (
        <svg {...common} className="text-success">
          <circle cx="6" cy="6" r="5.2" fill="currentColor" />
          <path
            d="M3.6 6.1l1.6 1.6 3.2-3.3"
            fill="none"
            stroke="var(--background)"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>
      );
    default:
      return (
        <svg {...common} className="text-muted-foreground">
          <circle cx="6" cy="6" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      );
  }
}

/** RAK-91: thin progress bar; decorative, the detail line says the same in words. */
function ProgressBar({
  progress,
  className,
}: {
  progress: BotTodoDetails["progress"];
  className?: string;
}) {
  if (!progress || !(progress.total > 0)) return null;
  const share = Math.min(100, Math.max(0, (progress.done / progress.total) * 100));
  return (
    <span
      aria-hidden="true"
      data-testid="bot-todo-progress"
      className={cn("block h-1 overflow-hidden rounded-full bg-border", className)}
    >
      <span
        className="block h-full rounded-full bg-status-working"
        style={{ width: `${share}%` }}
      />
    </span>
  );
}

/**
 * RAK-91: what the rail may do with a row (tickets.pause/resume/remove, RAK-92). Passed in so the
 * window stays free of transport; without it no buttons show. pendingIds disables a row's buttons
 * while its action runs.
 */
export type TodoRowActions = {
  onPause: (id: string) => void;
  onResume: (id: string) => void;
  onRemove: (id: string) => void;
  pendingIds?: ReadonlySet<string>;
};

/** RAK-91: look of a bot in the team view (avatar color and roster status), from the Shell. */
export type TodoOwnerLook = (botId: string) => { color?: string; status?: string } | undefined;

const ACTION_BUTTON =
  "grid size-6 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-foreground/10 hover:text-foreground disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-ring focus-visible:-outline-offset-2";

/**
 * RAK-91 (Johann): icon-only buttons, the name as aria-label and tooltip. Pause for open rows,
 * Resume for every blocked row, Cancel (closes the ticket) after a confirmation inside the row:
 * check confirms, back or Esc returns (focus back on Cancel), a confirmed cancel hands the focus to
 * onConfirmed (G1). Done rows have no actions.
 */
function TodoRowActionButtons({
  row,
  actions,
  onConfirmed,
}: {
  row: BotTodoRow;
  actions: TodoRowActions;
  /** G1: after "Yes, cancel" the row's buttons go away; this moves the focus to a fixed target. */
  onConfirmed?: () => void;
}) {
  const { t } = useLingui();
  const [confirming, setConfirming] = useState(false);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const wasConfirming = useRef(false);
  useEffect(() => {
    if (confirming) confirmRef.current?.focus();
    else if (wasConfirming.current) cancelRef.current?.focus();
    wasConfirming.current = confirming;
  }, [confirming]);
  const id = row.id;
  if (!id || row.status === "done") return null;
  const pending = actions.pendingIds?.has(id) ?? false;
  const ref = row.ref;
  if (confirming) {
    return (
      <fieldset
        aria-label={t`Cancel ${ref}?`}
        data-testid="bot-todo-confirm"
        className="m-0 flex min-w-0 items-center gap-0.5 border-0 p-0"
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          event.preventDefault();
          event.stopPropagation();
          setConfirming(false);
        }}
      >
        <span className="px-1 text-xs whitespace-nowrap text-destructive">{t`Cancel?`}</span>
        <button
          ref={confirmRef}
          type="button"
          aria-label={t`Yes, cancel ${ref}`}
          title={t`Yes, cancel`}
          disabled={pending}
          onClick={() => {
            wasConfirming.current = false;
            setConfirming(false);
            actions.onRemove(id);
            onConfirmed?.();
          }}
          className={cn(ACTION_BUTTON, "text-destructive hover:text-destructive")}
        >
          <Check size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label={t`Back`}
          title={t`Back`}
          onClick={() => setConfirming(false)}
          className={ACTION_BUTTON}
        >
          <Undo2 size={14} aria-hidden="true" />
        </button>
      </fieldset>
    );
  }
  const blocked = row.status === "blocked";
  return (
    <span className="flex items-center gap-0.5" data-testid="bot-todo-actions">
      {blocked ? (
        <button
          type="button"
          aria-label={t`Resume ${ref}`}
          title={t`Resume`}
          disabled={pending}
          onClick={() => actions.onResume(id)}
          className={ACTION_BUTTON}
        >
          <Play size={14} aria-hidden="true" fill="currentColor" />
        </button>
      ) : (
        <button
          type="button"
          aria-label={t`Pause ${ref}`}
          title={t`Pause`}
          disabled={pending}
          onClick={() => actions.onPause(id)}
          className={ACTION_BUTTON}
        >
          <Pause size={14} aria-hidden="true" fill="currentColor" />
        </button>
      )}
      <button
        ref={cancelRef}
        type="button"
        aria-label={t`Cancel ${ref}`}
        title={t`Cancel`}
        disabled={pending}
        onClick={() => setConfirming(true)}
        className={cn(ACTION_BUTTON, "hover:text-destructive")}
      >
        <Square size={12} aria-hidden="true" fill="currentColor" />
      </button>
    </span>
  );
}

/** RAK-91: true while the element is at least TODO_RAIL_WIDE_PX wide (measured, not guessed). */
function useIsWide(node: HTMLElement | null): boolean {
  const [wide, setWide] = useState(false);
  useLayoutEffect(() => {
    if (!node) return;
    const measure = () => setWide(node.getBoundingClientRect().width >= TODO_RAIL_WIDE_PX);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return wide;
}

/**
 * PIV-48: small, collapsible to-do window of one bot, rows ordered by orderTodoItems.
 * Labels are rendered as text only, never as HTML. Arbeitsmodell: a row is highlighted as working
 * only while its commitment runs; otherwise its details say why it does not (useCommitmentLine).
 * RAK-91: narrower than TODO_RAIL_WIDE_PX a click on a row opens its details below it, one
 * at a time; from that width on every row shows its details. `wide` overrides the measurement.
 * Team view (Johann: variant A): rows grouped by owner under a head with avatar and name in a
 * fixed column. `team` is the contract's flag (RAK-92); without it, rows of another bot than
 * selfBotId switch the view (G2). A confirmed cancel moves the focus to the list head (G1).
 */
export function BotTodoWindow({
  botName,
  items,
  collapsed,
  onCollapsedChange,
  className,
  wide: wideProp,
  selfBotId,
  ownerLook,
  actions,
  team: teamProp,
  requests,
}: {
  botName: string;
  items: readonly BotTodoRow[];
  /** Arbeitsmodell: the person's requests that still need someone, shown above the tasks. */
  requests?: readonly BotRequestItem[];
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  className?: string;
  wide?: boolean;
  /** The viewed bot; rows of other owners switch the window to the team view. */
  selfBotId?: string;
  ownerLook?: TodoOwnerLook;
  actions?: TodoRowActions;
  /** bots.todo's `team` (RAK-92); null or missing falls back to the rows' owners. */
  team?: boolean | null;
}) {
  const { t } = useLingui();
  const commitmentLine = useCommitmentLine();
  const words = useTodoDetailWords();
  const headRef = useRef<HTMLButtonElement>(null);
  const focusHead = () => headRef.current?.focus();
  const listId = useId();
  const [section, setSection] = useState<HTMLElement | null>(null);
  const measuredWide = useIsWide(section);
  const wide = wideProp ?? measuredWide;
  const [openKey, setOpenKey] = useState<string | null>(null);
  const rows = orderTodoItems(items);
  if (rows.length === 0 && !requests?.length) return null;
  const requestWord: Record<BotRequestItem["status"], string> = {
    unanswered: t`Unanswered`,
    asked: t`Waiting for your answer`,
    open: t`Not finished`,
  };
  const openCount = rows.filter((row) => row.status !== "done").length;
  const doing = rows.find(isWorkingRow);
  const statusWord: Record<BotTodoStatus, string> = {
    todo: t`coming up`,
    doing: t`in progress`,
    review: t`in review`,
    blocked: t`blocked`,
    done: t`done`,
  };
  const team =
    teamProp ?? rows.some((row) => row.ownerBotId != null && row.ownerBotId !== selfBotId);
  const groups = team ? groupTodoRowsByOwner(rows, selfBotId) : [];
  // Teil A delivers ids (refs before RAK-92) unique per board, so they are stable keys.
  const renderRow = (row: BotTodoRow, groupOpen: number) => {
    const key = row.id ?? row.ref;
    const open = wide || openKey === key;
    const detailsId = `${listId}-details-${key}`;
    const line = formatTodoDetailLine(row, words);
    const reason = row.status === "blocked" ? row.blockedReason?.trim() || null : null;
    const working = isWorkingRow(row);
    const stateLine = commitmentLine(row.state);
    const cells = (
      <>
        <span className="grid size-4 place-items-center">
          <StatusIcon status={row.status} />
        </span>
        <span
          className={cn(
            "text-xs tabular-nums",
            working
              ? "text-status-working"
              : row.status === "done"
                ? "text-success"
                : "text-muted-foreground",
          )}
        >
          {row.ref}
        </span>
        <span
          className={cn(
            "truncate",
            row.status === "doing" && !working ? "text-foreground" : LABEL_TONE[row.status],
          )}
          title={row.label}
        >
          {row.label}
          <span className="sr-only">, {statusWord[row.status]}</span>
        </span>
      </>
    );
    const rowClass = cn(
      "grid w-full items-center gap-1.5 rounded-lg px-1.5 py-1 text-start",
      working && "bg-status-working/10",
    );
    return (
      <li key={key} className="contents">
        {row.status === "done" && groupOpen > 0 ? (
          <div aria-hidden="true" className="mx-2 my-1 h-px bg-border" />
        ) : null}
        {wide ? (
          <>
            <div
              data-status={row.status}
              className={cn(rowClass, "grid-cols-[16px_52px_1fr_auto]")}
            >
              {cells}
              {actions ? (
                <TodoRowActionButtons row={row} actions={actions} onConfirmed={focusHead} />
              ) : (
                <span />
              )}
            </div>
            {line || reason || stateLine ? (
              <div
                data-testid="bot-todo-details"
                className="ms-[86px] me-1.5 mb-1.5 text-xs text-muted-foreground"
              >
                {line ? (
                  <p className="m-0 flex items-center gap-2.5 tabular-nums">
                    <ProgressBar progress={row.progress} className="w-[90px] shrink-0" />
                    <span>{line}</span>
                  </p>
                ) : null}
                {reason ? <p className="m-0 mt-0.5 text-destructive">{reason}</p> : null}
                {stateLine ? <p className="m-0 mt-0.5">{stateLine}</p> : null}
              </div>
            ) : null}
          </>
        ) : (
          <>
            <button
              type="button"
              data-status={row.status}
              aria-expanded={open}
              aria-controls={detailsId}
              onClick={() => setOpenKey(open ? null : key)}
              className={cn(
                rowClass,
                "grid-cols-[16px_52px_1fr] hover:bg-foreground/5 focus-visible:outline-2 focus-visible:outline-ring focus-visible:-outline-offset-2",
              )}
            >
              {cells}
            </button>
            <div
              id={detailsId}
              hidden={!open}
              data-testid="bot-todo-details"
              className="ms-7 me-1.5 mt-0.5 mb-1.5 rounded-lg bg-muted px-2.5 py-2 text-xs text-muted-foreground"
            >
              <ProgressBar progress={row.progress} className="mb-1.5" />
              <p className="m-0 text-foreground tabular-nums">{line ?? t`No details yet`}</p>
              {reason ? <p className="m-0 mt-1 text-destructive">{reason}</p> : null}
              {stateLine ? <p className="m-0 mt-1">{stateLine}</p> : null}
              {actions && row.id && row.status !== "done" ? (
                <div className="mt-1.5 flex justify-end">
                  <TodoRowActionButtons row={row} actions={actions} onConfirmed={focusHead} />
                </div>
              ) : null}
            </div>
          </>
        )}
      </li>
    );
  };
  return (
    <section
      ref={setSection}
      aria-label={t`Tasks of ${botName}`}
      data-testid="bot-todo-window"
      data-collapsed={collapsed}
      data-wide={wide}
      className={cn(
        "pointer-events-auto w-[min(268px,100%)] rounded-xl border border-border bg-background text-[13px] shadow-[0_4px_16px_#0000000f]",
        className,
      )}
    >
      <button
        ref={headRef}
        type="button"
        aria-expanded={!collapsed}
        aria-controls={listId}
        onClick={() => onCollapsedChange(!collapsed)}
        className="flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-start focus-visible:outline-2 focus-visible:outline-ring focus-visible:-outline-offset-2"
      >
        <ChevronDown
          size={12}
          aria-hidden="true"
          className={cn(
            "shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none",
            collapsed && "-rotate-90 rtl:rotate-90",
          )}
        />
        <span className="flex min-w-0 flex-1 items-center gap-1.5 text-xs font-semibold tracking-[0.02em]">
          <span>{t`To-do`}</span>
          <span className="font-medium text-muted-foreground">· {t`${openCount} open`}</span>
          {collapsed ? (
            <span className="truncate font-medium text-status-working">
              · {doing ? `${doing.ref} ${doing.label}` : t`nothing in progress`}
            </span>
          ) : null}
        </span>
      </button>
      {requests?.length && !collapsed ? (
        <ul data-testid="bot-todo-requests" className="m-0 list-none px-2.5 pb-1 text-xs">
          {requests.map((request) => (
            <li key={request.id} className="flex gap-1.5 py-0.5" title={request.excerpt}>
              <span
                className={cn(
                  "shrink-0 font-medium",
                  request.status === "asked" ? "text-status-review" : "text-destructive",
                )}
              >
                {requestWord[request.status]}
              </span>
              <span className="truncate text-muted-foreground">{request.excerpt}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {team ? (
        <div id={listId} hidden={collapsed} className="px-1 pb-1.5">
          {groups.map((group, groupIndex) => {
            const own = group.ownerBotId === (selfBotId ?? null);
            const name = group.ownerName ?? (own ? botName : "–");
            const groupOpen = group.items.filter((row) => row.status !== "done").length;
            const busy = group.items.some((row) => row.working);
            const look = group.ownerBotId ? ownerLook?.(group.ownerBotId) : undefined;
            const headId = `${listId}-group-${groupIndex}`;
            return (
              <div key={group.ownerBotId ?? ""} data-testid="bot-todo-group">
                <h3
                  id={headId}
                  className="m-0 grid grid-cols-[18px_1fr] items-center gap-1.5 px-1.5 pt-2 pb-0.5 text-xs font-semibold"
                >
                  <BotAvatar
                    color={look?.color ?? ""}
                    identity={group.ownerBotId ?? undefined}
                    size={18}
                    status={busy ? "running" : look?.status}
                  />
                  <span className="min-w-0 truncate">
                    {name}
                    <span className="font-medium text-muted-foreground">
                      {" "}
                      · {t`${groupOpen} open`}
                    </span>
                    {busy ? (
                      <span className="font-medium text-status-working"> · {t`working`}</span>
                    ) : null}
                  </span>
                </h3>
                <ol aria-labelledby={headId} className="m-0 list-none">
                  {group.items.map((row) => renderRow(row, groupOpen))}
                </ol>
              </div>
            );
          })}
        </div>
      ) : (
        <ol id={listId} hidden={collapsed} className="m-0 list-none px-1 pb-1.5">
          {rows.map((row) => renderRow(row, openCount))}
        </ol>
      )}
    </section>
  );
}

/**
 * RAK-91: grip on the inner edge of the side rail. Dragging, arrow keys (Shift: four steps),
 * Home/End set the width; a double click resets it to the minimum. onWidthChange gets
 * commit=true when the width should be stored (drag end, key, reset).
 */
function TodoRailGrip({
  width,
  onWidthChange,
}: {
  width: number;
  onWidthChange: (width: number, commit: boolean) => void;
}) {
  const { t } = useLingui();
  const ref = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  function bounds() {
    const slot = ref.current?.parentElement;
    const container = slot?.parentElement;
    return {
      slot,
      rtl: slot ? getComputedStyle(slot).direction === "rtl" : false,
      windowWidth: window.innerWidth,
      containerWidth: container?.clientWidth ?? window.innerWidth,
    };
  }

  function widthAt(clientX: number): number {
    const { slot, rtl, windowWidth, containerWidth } = bounds();
    if (!slot) return width;
    const box = slot.getBoundingClientRect();
    // In right-to-left layouts the rail sits on the left and the grip on its right edge.
    const next = rtl ? clientX - box.left : box.right - clientX;
    return clampTodoRailWidth(next, windowWidth, containerWidth);
  }

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragging(true);
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    if (dragging) onWidthChange(widthAt(event.clientX), false);
  }

  function onPointerEnd(event: PointerEvent<HTMLDivElement>) {
    if (!dragging) return;
    setDragging(false);
    onWidthChange(widthAt(event.clientX), true);
    if (event.currentTarget.hasPointerCapture?.(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const { rtl, windowWidth, containerWidth } = bounds();
    const step = TODO_RAIL_KEY_STEP_PX * (event.shiftKey ? 4 : 1);
    // The arrow pointing away from the rail widens it, in either direction.
    const widen = rtl ? "ArrowRight" : "ArrowLeft";
    const shrink = rtl ? "ArrowLeft" : "ArrowRight";
    let next: number | null = null;
    if (event.key === widen) next = width + step;
    if (event.key === shrink) next = width - step;
    if (event.key === "Home") next = Number.POSITIVE_INFINITY;
    if (event.key === "End") next = TODO_RAIL_MIN_PX;
    if (next === null) return;
    event.preventDefault();
    const max = maxTodoRailWidth(windowWidth, containerWidth);
    onWidthChange(
      next === Number.POSITIVE_INFINITY
        ? max
        : clampTodoRailWidth(next, windowWidth, containerWidth),
      true,
    );
  }

  // Shown values follow the CSS clamp, so a stored width wider than the window reads truthfully.
  const [limits, setLimits] = useState(() => ({
    windowWidth: window.innerWidth,
    containerWidth: window.innerWidth,
  }));
  useLayoutEffect(() => {
    const container = ref.current?.parentElement?.parentElement;
    const measure = () =>
      setLimits({
        windowWidth: window.innerWidth,
        containerWidth: container?.clientWidth ?? window.innerWidth,
      });
    measure();
    window.addEventListener("resize", measure);
    const observer =
      container && typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (container) observer?.observe(container);
    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, []);
  const max = maxTodoRailWidth(limits.windowWidth, limits.containerWidth);
  const shown = clampTodoRailWidth(width, limits.windowWidth, limits.containerWidth);
  return (
    // biome-ignore lint/a11y/useSemanticElements: a focusable, draggable vertical splitter has no native element.
    <div
      ref={ref}
      role="separator"
      aria-orientation="vertical"
      aria-label={t`Resize task list`}
      aria-valuemin={TODO_RAIL_MIN_PX}
      aria-valuemax={max}
      aria-valuenow={shown}
      tabIndex={0}
      title={t`Drag to resize, double-click to reset`}
      data-testid="bot-todo-grip"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onKeyDown={onKeyDown}
      onDoubleClick={() => onWidthChange(TODO_RAIL_MIN_PX, true)}
      className="group absolute inset-y-2 -start-2.5 z-10 hidden w-3 cursor-col-resize touch-none rounded-full outline-none md:block"
    >
      <span
        aria-hidden="true"
        className={cn(
          "absolute inset-y-0 start-1/2 w-0.5 -translate-x-1/2 rounded-full transition-colors motion-reduce:transition-none group-hover:bg-ring group-focus-visible:bg-ring rtl:translate-x-1/2",
          dragging ? "bg-ring" : "bg-transparent",
        )}
      />
    </div>
  );
}

/** True for an RPC error whose code is NOT_FOUND (a foreign or deleted ticket id). */
function isNotFound(error: unknown): boolean {
  return Boolean(
    error && typeof error === "object" && "code" in error && error.code === "NOT_FOUND",
  );
}

/**
 * PIV-48: the window for the open bot, rows from bots.todo. Desktop (md and up): in the side
 * rail at the top right, the reading column moves left beside it (see --chat-side-rail in Shell).
 * Phones: a full-width row under the floating header. While the bot has no tasks or sessions only
 * the session list shows, where the person can start a session.
 * RAK-91: on desktop the rail width comes from --todo-rail-w (set by Shell, clamped in CSS so
 * a smaller window never squeezes the reading column); the grip changes it. Pause, resume and
 * cancel go to tickets.pause/resume/remove (useTodoRowActions), failures as a toast (NOT_FOUND
 * with an own translated text, the same for foreign and deleted ids); the team view follows
 * bots.todo's team flag. Sitzungen: the bot's work sessions are listed below the tasks.
 */
export function ActiveBotTodo({
  botId,
  botName,
  collapsed,
  onCollapsedChange,
  railWidth,
  onRailWidthChange,
  ownerLook,
}: {
  botId: string;
  botName: string;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  railWidth: number;
  onRailWidthChange: (width: number, commit: boolean) => void;
  ownerLook?: TodoOwnerLook;
}) {
  const { t } = useLingui();
  const { items, team } = useBotTodo(botId);
  // A foreign or deleted id answers NOT_FOUND with an English server sentence; show our own
  // translated text instead, the same for both cases (no hint whether the ticket exists).
  const actions = useTodoRowActions((error) =>
    toast.error(
      isNotFound(error)
        ? t`This task is no longer available.`
        : errorText(error, t`Could not change the task. Try again.`),
    ),
  );
  const requests = useBotTodoRequests(botId);
  const sessions = useBotSessions(botId);
  const board = useSyncExternalStore(
    subscribeTicketBoardEnabled,
    getTicketBoardEnabled,
    () => false,
  );
  // Sitzungen: a bot with nothing to do still shows its session list, to start a session there.
  if (items.length === 0 && requests.length === 0 && sessions.length === 0)
    return !board ? null : (
      <div
        data-testid="bot-todo-slot"
        data-side-rail=""
        className="flex shrink-0 px-3 pt-16 md:absolute md:top-[60px] md:end-4 md:z-20 md:w-(--todo-rail) md:p-0"
      >
        <BotSessionsList botId={botId} />
      </div>
    );
  return (
    <div
      data-testid="bot-todo-slot"
      data-side-rail=""
      className="flex shrink-0 px-3 pt-16 md:absolute md:top-[60px] md:end-4 md:z-20 md:w-(--todo-rail) md:p-0"
    >
      <TodoRailGrip width={railWidth} onWidthChange={onRailWidthChange} />
      <div className="flex w-full flex-col">
        <BotTodoWindow
          botName={botName}
          items={items}
          collapsed={collapsed}
          onCollapsedChange={onCollapsedChange}
          className="w-full"
          selfBotId={botId}
          ownerLook={ownerLook}
          actions={actions}
          team={team}
          requests={requests}
        />
        {/* jhl-Bots (Sitzungen): each ticket's own session chat. */}
        <BotSessionsList botId={botId} />
      </div>
    </div>
  );
}
